import { useState, useMemo, useEffect } from 'react';
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faTruck } from '@fortawesome/free-solid-svg-icons';
import { getMapUrl } from '@s20/common/util/dom';

const getEmpCoords = (emp) => {
    const lat = emp.homeAddress?.latitude ?? null;
    const lng = emp.homeAddress?.longitude ?? null;

    if (lat == null || lng == null) return null;
    return { lat, lng };
};

const getAssignedEquipmentDetails = (emp) => {
    if (!emp?.assignedEquipment || emp.assignedEquipment.length === 0) {
        return null;
    }
    const workTrucks = emp.assignedEquipment.filter(
        (e) => e?.equipmentType?.isVehicle,
    );
    const flagEquipments = emp.assignedEquipment.filter(
        (e) => e?.equipmentType?.equipmentTypeAFADType?.isGSF,
    );
    if (workTrucks.length === 0 && flagEquipments.length === 0)
        return null;
    const truckDetails = workTrucks.map(
        (wt) => `${wt.equipmentType?.description} - ${wt.identifier}`,
    );
    const flagDetails = flagEquipments.map(
        (fe) => `${fe.equipmentType?.description} - ${fe.identifier}`,
    );
    return [...truckDetails, ...flagDetails].join(', ');
};

export const MapMarkers = ({ employees, selectedEmployeeId }) => {
    const [selected, setSelected] = useState(null);
    const list = useMemo(() => Array.isArray(employees) ? employees : [], [employees]);

    useEffect(() => {
        setSelected(null);
    }, [employees]);

    useEffect(() => {
        if (!selectedEmployeeId) {
            setSelected(null);
            return;
        }
        const emp = list.find((e) => e.id === selectedEmployeeId);
        if (emp) {
            setSelected(emp);
        }
    }, [selectedEmployeeId, list]);

    const infoPos = selected ? getEmpCoords(selected) : null;

    const homeAddress = [
        selected?.homeAddress?.address1,
        selected?.homeAddress?.address2,
        selected?.homeAddress?.city,
        selected?.homeAddress?.state,
        selected?.homeAddress?.zip,
    ].filter(Boolean).join(', ') || '-';

    return (
        <>
            {list.map((emp, i) => {
                const pos = getEmpCoords(emp);
                if (!pos) return null;
                const hasTruck = !!emp.assignedEquipment?.some((e) => e?.equipmentType?.isVehicle);
                return (
                    <AdvancedMarker
                        key={emp.id ?? i}
                        position={pos}
                        onClick={() => setSelected(emp)}
                        title={`${emp.lastName}, ${emp.firstName}`}
                        collisionBehavior="REQUIRED"
                    >
                        <FontAwesomeIcon
                            icon={hasTruck ? faTruck : faMapMarkerAlt}
                            size="lg"
                        />
                    </AdvancedMarker>
                );
            })}

            {selected && infoPos && (
                <InfoWindow
                    position={infoPos}
                    onCloseClick={() => setSelected(null)}
                >
                    <div className="infoWindow">
                        {/* Name */}
                        <div className="mb-1">
                            <div className="text-muted text-uppercase small">Name</div>
                            <div className="fw-semibold">
                                {selected.lastName}, {selected.firstName}
                            </div>
                        </div>

                        {/* Email */}
                        <div className="mb-1">
                            <div className="text-muted text-uppercase small">Email</div>
                            <div className="fw-semibold text-break">
                                {selected.email}
                            </div>
                        </div>

                        {/* Address */}
                        <div className="mb-1">
                            <div className="text-muted text-uppercase small">Address</div>
                            <div className="fw-semibold text-break">
                                <a
                                    href={getMapUrl(homeAddress)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="site-link"
                                >
                                    {homeAddress}
                                </a>
                            </div>
                        </div>

                        {/* Dispatch company */}
                        <div className="mb-1">
                            <div className="text-muted text-uppercase small">Dispatching</div>
                            <div className="fw-semibold">
                                {selected.dispatchCompany}
                            </div>
                        </div>

                        {/* Equipment (optional) */}
                        {getAssignedEquipmentDetails(selected)?.length > 0 && (
                            <div className="mb-1">
                                <div className="text-muted text-uppercase small">Equipment</div>
                                <div className="fw-semibold">
                                    {getAssignedEquipmentDetails(selected)}
                                </div>
                            </div>
                        )}
                    </div>
                </InfoWindow>
            )}
        </>
    );
};